import React from 'react';
import { Table, Tabs, Card, Typography, Input, Button } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import './ProductStatsTable.css';
import type {
  IDailyProductStat,
  PlanEditStates,
  ProductStatsData,
  TableDataTypeWithDays,
} from '../../types/stats';

const { Paragraph, Text } = Typography;

// --- Constants ---
const metricsOrder = [
  'sum',
  'views',
  'clicks',
  'atbs',
  'orders',
  'costPerAdvertisingOrder',
  'openCardCount',
  'addToCartCount',
  'ordersCount',
  'buyoutsCount',
  'ordersSumRub',
  'buyoutsSumRub',
  'organicClicksCount',
  'organicOrdersCount',
  'price',
  'ctrPercentage',
  'cartPercentage',
  'orderPercentage',
  'buyoutPercentage',
  'drrPercentage',
];

const metricDisplayNames: Record<string, string> = {
  sum: 'Реклама затраты, ₽',
  views: 'Показы реклама',
  clicks: 'Клики реклама',
  atbs: 'Корзина реклама',
  orders: 'Заказы реклама',
  costPerAdvertisingOrder: 'Стоимость 1 заказа по рекламе',
  openCardCount: 'Клики всего',
  addToCartCount: 'Корзина всего',
  ordersCount: 'Заказы всего, шт',
  buyoutsCount: 'Выкупы всего, шт',
  ordersSumRub: 'Заказов всего, ₽',
  buyoutsSumRub: 'Выкупов всего, ₽',
  organicClicksCount: 'Клики органика',
  organicOrdersCount: 'Заказы органика, шт',
  price: 'Цена',
  ctrPercentage: '% CTR рекламы',
  cartPercentage: '% в корзину',
  orderPercentage: '% в заказ',
  buyoutPercentage: '% выкупа',
  drrPercentage: '% ДРР заказ',
};

// --- Helper Functions ---
const getSortedMonthKeys = (monthsData: Record<string, IDailyProductStat[]>): string[] => {
  const currentMonth = dayjs().format('YYYY-MM');
  const monthKeys = Object.keys(monthsData);

  return monthKeys.sort((a, b) => {
    if (a === currentMonth) return -1;
    if (b === currentMonth) return 1;
    return dayjs(b, 'YYYY-MM').valueOf() - dayjs(a, 'YYYY-MM').valueOf();
  });
};

const getMonthNameInRussian = (monthKey: string) => {
  const monthNames: Record<string, string> = {
    '01': 'Январь',
    '02': 'Февраль',
    '03': 'Март',
    '04': 'Апрель',
    '05': 'Май',
    '06': 'Июнь',
    '07': 'Июль',
    '08': 'Август',
    '09': 'Сентябрь',
    '10': 'Октябрь',
    '11': 'Ноябрь',
    '12': 'Декабрь',
  };
  const [year, month] = monthKey.split('-');
  const monthName = monthNames[month] || dayjs(monthKey, 'YYYY-MM').format('MMMM');
  return `${monthName} ${year}`;
};

const generateTableDataSource = (
  monthlyStats: IDailyProductStat[],
  currentMonth: string,
  productId: number,
  planEditStates: PlanEditStates
): TableDataTypeWithDays[] => {
  if (!monthlyStats || !monthlyStats.length) return [];
  const statsByDate: Record<string, IDailyProductStat> = {};
  monthlyStats.forEach((stat) => {
    const day = dayjs(stat.date).date();
    statsByDate[String(day)] = stat;
  });

  const monthlyTotals: Record<string, number> = {};
  metricsOrder.forEach((metricKey) => {
    let total = 0;
    const daysInMonth = dayjs(currentMonth, 'YYYY-MM').daysInMonth();
    for (let day = 1; day <= daysInMonth; day++) {
      const dayStat = statsByDate[String(day)];
      if (dayStat) {
        const value = (dayStat as unknown as Record<string, unknown>)[metricKey];
        if (value !== undefined && value !== null) {
          const numValue = Number(value);
          if (!isNaN(numValue)) {
            total += numValue;
          }
        }
      }
    }
    monthlyTotals[metricKey] = total;
  });

  return metricsOrder.map((metricKey) => {
    const row: TableDataTypeWithDays = {
      key: metricKey,
      metric: metricDisplayNames[metricKey],
    };
    const isCampaignMetric = ['sum', 'views', 'clicks', 'atbs', 'orders'].includes(metricKey);
    const isNmMetric = [
      'openCardCount',
      'addToCartCount',
      'ordersCount',
      'buyoutsCount',
      'ordersSumRub',
      'buyoutsSumRub',
    ].includes(metricKey);
    const isCalculatedMetric = [
      'costPerAdvertisingOrder',
      'organicClicksCount',
      'organicOrdersCount',
      'price',
    ].includes(metricKey);
    const isPercentageMetric = [
      'ctrPercentage',
      'cartPercentage',
      'orderPercentage',
      'buyoutPercentage',
      'drrPercentage',
    ].includes(metricKey);

    row.rowType = isCampaignMetric
      ? 'campaign'
      : isNmMetric
        ? 'nm'
        : isCalculatedMetric
          ? 'calculated'
          : isPercentageMetric
            ? 'percentage'
            : 'other';

    const planModelFields = [
      'sum',
      'costPerAdvertisingOrder',
      'ordersCount',
      'buyoutsCount',
      'ordersSumRub',
      'buyoutsSumRub',
      'price',
    ];
    if (planModelFields.includes(metricKey)) {
      // Handle calculated plan fields
      if (metricKey === 'ordersSumRub') {
        // (Заказы всего, шт / План) × (Цена / План)
        const ordersCountPlan =
          planEditStates[productId]?.[currentMonth]?.['ordersCount']?.value || 0;
        const pricePlan = planEditStates[productId]?.[currentMonth]?.['price']?.value || 0;
        row.plan = Math.round(Number(ordersCountPlan) * Number(pricePlan));
      } else if (metricKey === 'buyoutsSumRub') {
        // (Выкупы всего, шт / План) × (Цена / План)
        const buyoutsCountPlan =
          planEditStates[productId]?.[currentMonth]?.['buyoutsCount']?.value || 0;
        const pricePlan = planEditStates[productId]?.[currentMonth]?.['price']?.value || 0;
        row.plan = Math.round(Number(buyoutsCountPlan) * Number(pricePlan));
      } else {
        // Regular editable plan fields
        const editState = planEditStates[productId]?.[currentMonth]?.[metricKey] || {
          isEditing: false,
          value: 0,
        };
        if (editState.value) {
          row.plan = editState.value;
        }
      }
    }

    const daysInMonth = dayjs(currentMonth, 'YYYY-MM').daysInMonth();
    for (let day = 1; day <= daysInMonth; day++) {
      const dayStat = statsByDate[String(day)];
      if (dayStat) {
        if (metricKey === 'costPerAdvertisingOrder') {
          const daySum = (dayStat as unknown as Record<string, unknown>)['sum'];
          const dayOrders = (dayStat as unknown as Record<string, unknown>)['orders'];
          const sumValue = daySum !== undefined && daySum !== null ? Number(daySum) : 0;
          const ordersValue = dayOrders !== undefined && dayOrders !== null ? Number(dayOrders) : 0;
          row[`day${day}`] =
            ordersValue === 0 ? Math.round(sumValue) : Math.round(sumValue / ordersValue);
        } else if (metricKey === 'organicClicksCount') {
          const openCardCount = (dayStat as unknown as Record<string, unknown>)['openCardCount'];
          const clicks = (dayStat as unknown as Record<string, unknown>)['clicks'];
          row[`day${day}`] = Math.round(
            (openCardCount !== undefined && openCardCount !== null ? Number(openCardCount) : 0) -
              (clicks !== undefined && clicks !== null ? Number(clicks) : 0)
          );
        } else if (metricKey === 'organicOrdersCount') {
          const ordersCount = (dayStat as unknown as Record<string, unknown>)['ordersCount'];
          const orders = (dayStat as unknown as Record<string, unknown>)['orders'];
          row[`day${day}`] = Math.round(
            (ordersCount !== undefined && ordersCount !== null ? Number(ordersCount) : 0) -
              (orders !== undefined && orders !== null ? Number(orders) : 0)
          );
        } else if (metricKey === 'price') {
          const ordersSumRub = (dayStat as unknown as Record<string, unknown>)['ordersSumRub'];
          const ordersCount = (dayStat as unknown as Record<string, unknown>)['ordersCount'];
          const ordersSumValue =
            ordersSumRub !== undefined && ordersSumRub !== null ? Number(ordersSumRub) : 0;
          const ordersCountValue =
            ordersCount !== undefined && ordersCount !== null ? Number(ordersCount) : 0;
          row[`day${day}`] =
            ordersCountValue === 0 ? 0 : Math.round(ordersSumValue / ordersCountValue);
        } else if (metricKey === 'ctrPercentage') {
          const clicks = (dayStat as unknown as Record<string, unknown>)['clicks'];
          const views = (dayStat as unknown as Record<string, unknown>)['views'];
          const clicksValue = clicks !== undefined && clicks !== null ? Number(clicks) : 0;
          const viewsValue = views !== undefined && views !== null ? Number(views) : 0;
          const percentage = (clicksValue / viewsValue) * 100;
          row[`day${day}`] = viewsValue === 0 ? '0.0%' : `${percentage.toFixed(1)}%`;
        } else if (metricKey === 'cartPercentage') {
          const addToCartCount = (dayStat as unknown as Record<string, unknown>)['addToCartCount'];
          const openCardCount = (dayStat as unknown as Record<string, unknown>)['openCardCount'];
          const addToCartValue =
            addToCartCount !== undefined && addToCartCount !== null ? Number(addToCartCount) : 0;
          const openCardValue =
            openCardCount !== undefined && openCardCount !== null ? Number(openCardCount) : 0;
          const percentage = (addToCartValue / openCardValue) * 100;
          row[`day${day}`] = openCardValue === 0 ? '0.0%' : `${percentage.toFixed(1)}%`;
        } else if (metricKey === 'orderPercentage') {
          const ordersCount = (dayStat as unknown as Record<string, unknown>)['ordersCount'];
          const addToCartCount = (dayStat as unknown as Record<string, unknown>)['addToCartCount'];
          const ordersCountValue =
            ordersCount !== undefined && ordersCount !== null ? Number(ordersCount) : 0;
          const addToCartValue =
            addToCartCount !== undefined && addToCartCount !== null ? Number(addToCartCount) : 0;
          const percentage = (ordersCountValue / addToCartValue) * 100;
          row[`day${day}`] = addToCartValue === 0 ? '0.0%' : `${percentage.toFixed(1)}%`;
        } else if (metricKey === 'buyoutPercentage') {
          const buyoutsSumRub = (dayStat as unknown as Record<string, unknown>)['buyoutsSumRub'];
          const ordersSumRub = (dayStat as unknown as Record<string, unknown>)['ordersSumRub'];
          const buyoutsSumValue =
            buyoutsSumRub !== undefined && buyoutsSumRub !== null ? Number(buyoutsSumRub) : 0;
          const ordersSumValue =
            ordersSumRub !== undefined && ordersSumRub !== null ? Number(ordersSumRub) : 0;
          const percentage = (buyoutsSumValue / ordersSumValue) * 100;
          row[`day${day}`] = ordersSumValue === 0 ? '0.0%' : `${percentage.toFixed(1)}%`;
        } else if (metricKey === 'drrPercentage') {
          const sum = (dayStat as unknown as Record<string, unknown>)['sum'];
          const ordersSumRub = (dayStat as unknown as Record<string, unknown>)['ordersSumRub'];
          const sumValue = sum !== undefined && sum !== null ? Number(sum) : 0;
          const ordersSumValue =
            ordersSumRub !== undefined && ordersSumRub !== null ? Number(ordersSumRub) : 0;
          const percentage = (sumValue / ordersSumValue) * 100;
          row[`day${day}`] = ordersSumValue === 0 ? '0.0%' : `${percentage.toFixed(1)}%`;
        } else {
          const value = (dayStat as unknown as Record<string, unknown>)[metricKey];
          row[`day${day}`] =
            value !== undefined && value !== null ? Math.round(Number(value)) || 0 : 0;
        }
      } else {
        row[`day${day}`] = 0;
      }
    }

    if (metricKey === 'costPerAdvertisingOrder') {
      const totalSum = monthlyTotals['sum'] || 0;
      const totalOrders = monthlyTotals['orders'] || 0;
      row.fact = totalOrders === 0 ? Math.round(totalSum) : Math.round(totalSum / totalOrders);
    } else if (metricKey === 'organicClicksCount') {
      row.fact = Math.round((monthlyTotals['openCardCount'] || 0) - (monthlyTotals['clicks'] || 0));
    } else if (metricKey === 'organicOrdersCount') {
      row.fact = Math.round((monthlyTotals['ordersCount'] || 0) - (monthlyTotals['orders'] || 0));
    } else if (metricKey === 'price') {
      const totalOrdersSumRub = monthlyTotals['ordersSumRub'] || 0;
      const totalOrdersCount = monthlyTotals['ordersCount'] || 0;
      row.fact = totalOrdersCount === 0 ? 0 : Math.round(totalOrdersSumRub / totalOrdersCount);
    } else if (metricKey === 'ctrPercentage') {
      const totalClicks = monthlyTotals['clicks'] || 0;
      const totalViews = monthlyTotals['views'] || 0;
      row.fact = totalViews === 0 ? '0.0%' : `${((totalClicks / totalViews) * 100).toFixed(1)}%`;
    } else if (metricKey === 'cartPercentage') {
      const totalAddToCart = monthlyTotals['addToCartCount'] || 0;
      const totalOpenCard = monthlyTotals['openCardCount'] || 0;
      row.fact =
        totalOpenCard === 0 ? '0.0%' : `${((totalAddToCart / totalOpenCard) * 100).toFixed(1)}%`;
    } else if (metricKey === 'orderPercentage') {
      const totalOrdersCount = monthlyTotals['ordersCount'] || 0;
      const totalAddToCart = monthlyTotals['addToCartCount'] || 0;
      row.fact =
        totalAddToCart === 0
          ? '0.0%'
          : `${((totalOrdersCount / totalAddToCart) * 100).toFixed(1)}%`;
    } else if (metricKey === 'buyoutPercentage') {
      const totalBuyoutsSumRub = monthlyTotals['buyoutsSumRub'] || 0;
      const totalOrdersSumRub = monthlyTotals['ordersSumRub'] || 0;
      row.fact =
        totalOrdersSumRub === 0
          ? '0.0%'
          : `${((totalBuyoutsSumRub / totalOrdersSumRub) * 100).toFixed(1)}%`;
    } else if (metricKey === 'drrPercentage') {
      const totalSum = monthlyTotals['sum'] || 0;
      const totalOrdersSumRub = monthlyTotals['ordersSumRub'] || 0;
      row.fact =
        totalOrdersSumRub === 0 ? '0.0%' : `${((totalSum / totalOrdersSumRub) * 100).toFixed(1)}%`;
    } else {
      row.fact = Math.round(monthlyTotals[metricKey] || 0);
    }

    return row;
  });
};

const generateTableColumns = (
  currentMonth: string,
  productId: number,
  planEditStates: PlanEditStates,
  updatePlanValue: (productId: number, monthKey: string, metricKey: string, value: string) => void,
  toggleEditMode: (productId: number, monthKey: string, metricKey: string) => void,
  monthlyStats: IDailyProductStat[]
): ColumnType<TableDataTypeWithDays>[] => {
  // Create statsByDate for daily column logic
  const statsByDate: Record<string, IDailyProductStat> = {};
  if (monthlyStats && monthlyStats.length > 0) {
    monthlyStats.forEach((stat) => {
      const day = dayjs(stat.date).date();
      statsByDate[String(day)] = stat;
    });
  }

  // Define key groups for styling
  const metricGreyA5Keys = [
    'costPerAdvertisingOrder',
    'organicClicksCount',
    'organicOrdersCount',
    'price',
  ];
  const metricGreyD8Keys = [
    'sum',
    'views',
    'clicks',
    'atbs',
    'orders',
    'openCardCount',
    'addToCartCount',
    'ordersCount',
    'buyoutsCount',
    'ordersSumRub',
    'buyoutsSumRub',
  ];
  const factGreyA5Keys = ['organicClicksCount', 'organicOrdersCount'];
  const percentageKeys = [
    'ctrPercentage',
    'cartPercentage',
    'orderPercentage',
    'buyoutPercentage',
    'drrPercentage',
  ];

  const borderKeys = ['costPerAdvertisingOrder', 'buyoutsSumRub', 'price', 'drrPercentage'];

  const columns: ColumnType<TableDataTypeWithDays>[] = [
    {
      title: 'Метрика',
      dataIndex: 'metric',
      key: 'metric',
      fixed: 'left',
      width: 300,
      render: (text: string) => <Text strong>{text}</Text>,
      onCell: (record: TableDataTypeWithDays) => {
        const style: React.CSSProperties = {};
        if (metricGreyA5Keys.includes(record.key)) {
          style.backgroundColor = '#A5A5A5';
        }
        if (metricGreyD8Keys.includes(record.key)) {
          style.backgroundColor = '#D8D8D8';
        }
        if (percentageKeys.includes(record.key)) {
          style.backgroundColor = '#BCD7ED';
        }
        if (borderKeys.includes(record.key)) {
          style.borderBottom = '2px solid #555';
        }
        return { style };
      },
    },
    {
      title: 'План',
      dataIndex: 'plan',
      key: 'plan',
      fixed: 'left',
      width: 80,
      align: 'right' as const,
      onCell: (record: TableDataTypeWithDays) => {
        const style: React.CSSProperties = {};

        // Check for required plan fields that need red background when not set
        const requiredPlanFields = ['ordersCount', 'buyoutsCount', 'price'];
        if (requiredPlanFields.includes(record.key)) {
          const editState = planEditStates[productId]?.[currentMonth]?.[record.key] || {
            isEditing: false,
            value: 0,
          };
          const planValue = editState.value ? Number(editState.value) : 0;
          if (planValue <= 0) {
            style.backgroundColor = '#ffcdd2'; // Red background when plan not set
          }
        }

        if (record.key === 'costPerAdvertisingOrder') {
          style.backgroundColor = '#a5d6a7';
        }
        if (percentageKeys.includes(record.key)) {
          style.backgroundColor = '#BCD7ED';
        }
        if (borderKeys.includes(record.key)) {
          style.borderBottom = '2px solid #555';
        }
        return { style };
      },
      render: (_, record: TableDataTypeWithDays) => {
        const planModelFields = [
          'sum',
          'costPerAdvertisingOrder',
          'ordersCount',
          'buyoutsCount',
          'ordersSumRub',
          'buyoutsSumRub',
          'price',
        ];
        if (!planModelFields.includes(record.key)) return '';

        const metricKey = record.key;

        // Calculated plan fields - no manual input allowed
        const calculatedPlanFields = ['ordersSumRub', 'buyoutsSumRub'];

        if (calculatedPlanFields.includes(metricKey)) {
          // Get the necessary plan values for calculation
          const ordersCountPlan =
            planEditStates[productId]?.[currentMonth]?.['ordersCount']?.value || 0;
          const buyoutsCountPlan =
            planEditStates[productId]?.[currentMonth]?.['buyoutsCount']?.value || 0;
          const pricePlan = planEditStates[productId]?.[currentMonth]?.['price']?.value || 0;

          let calculatedValue = 0;
          if (metricKey === 'ordersSumRub') {
            // (Заказы всего, шт / План) × (Цена / План)
            calculatedValue = Number(ordersCountPlan) * Number(pricePlan);
          } else if (metricKey === 'buyoutsSumRub') {
            // (Выкупы всего, шт / План) × (Цена / План)
            calculatedValue = Number(buyoutsCountPlan) * Number(pricePlan);
          }

          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                padding: '0 4px',
                fontStyle: 'italic',
                color: '#666',
              }}
            >
              <span>{Math.round(calculatedValue)}</span>
            </div>
          );
        }

        // Regular editable plan fields
        const editState = planEditStates[productId]?.[currentMonth]?.[metricKey] || {
          isEditing: false,
          value: 0,
        };

        return editState.isEditing ? (
          <Input
            size="small"
            value={editState.value}
            onChange={(e) => updatePlanValue(productId, currentMonth, metricKey, e.target.value)}
            onPressEnter={() => toggleEditMode(productId, currentMonth, metricKey)}
            autoFocus
            style={{
              width: '80px',
              backgroundColor: 'transparent',
            }}
            type="number"
          />
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0 4px',
            }}
          >
            <span>{editState.value || 0}</span>
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => toggleEditMode(productId, currentMonth, metricKey)}
              style={{ marginLeft: '4px' }}
            />
          </div>
        );
      },
    },
    {
      title: 'Факт',
      dataIndex: 'fact',
      key: 'fact',
      fixed: 'left',
      width: 70,
      align: 'right' as const,
      render: (value: number | string) => value || 0,
      onCell: (record: TableDataTypeWithDays) => {
        const style: React.CSSProperties = {};

        // Conditional red/green colors (highest priority)
        if (record.key === 'costPerAdvertisingOrder' || record.key === 'price') {
          const factValue = Number(record.fact) || 0;
          const editState = planEditStates[productId]?.[currentMonth]?.[record.key] || {
            isEditing: false,
            value: 0,
          };
          const planValue = editState.value ? Number(editState.value) : 0;
          if (planValue > 0) {
            if (record.key === 'costPerAdvertisingOrder') {
              style.backgroundColor = factValue > planValue ? '#ffcdd2' : '#a5d6a7';
            } else {
              style.backgroundColor = factValue > planValue ? '#a5d6a7' : '#ffcdd2';
            }
          }
        }

        // Grey backgrounds
        if (factGreyA5Keys.includes(record.key)) {
          style.backgroundColor = '#A5A5A5';
        }

        // Percentage background
        if (percentageKeys.includes(record.key)) {
          style.backgroundColor = '#BCD7ED';
        }

        // Border
        if (borderKeys.includes(record.key)) {
          style.borderBottom = '2px solid #555';
        }
        return { style };
      },
    },
    {
      title: 'План на сегодня',
      dataIndex: 'todayPlan',
      key: 'todayPlan',
      fixed: 'left',
      width: 136,
      align: 'right' as const,
      onCell: (record: TableDataTypeWithDays) => {
        const style: React.CSSProperties = {};

        // Check for required plan fields that need red background when not set
        const requiredPlanFields = ['ordersCount', 'buyoutsCount', 'price'];
        if (requiredPlanFields.includes(record.key)) {
          const editState = planEditStates[productId]?.[currentMonth]?.[record.key] || {
            isEditing: false,
            value: 0,
          };
          const planValue = editState.value ? Number(editState.value) : 0;
          if (planValue <= 0) {
            style.backgroundColor = '#ffcdd2'; // Red background when plan not set
          }
        }

        if (borderKeys.includes(record.key)) {
          style.borderBottom = '2px solid #555';
        }
        return { style };
      },
      render: (_, record: TableDataTypeWithDays) => {
        const targetMetrics = [
          'sum',
          'ordersCount',
          'buyoutsCount',
          'ordersSumRub',
          'buyoutsSumRub',
          'price',
        ];
        if (!targetMetrics.includes(record.key)) return '';

        const metricKey = record.key;

        // Get plan value - use calculated values for calculated fields
        let planValue = 0;
        if (metricKey === 'ordersSumRub') {
          // Use calculated plan: ordersCount × price
          const ordersCountPlan =
            planEditStates[productId]?.[currentMonth]?.['ordersCount']?.value || 0;
          const pricePlan = planEditStates[productId]?.[currentMonth]?.['price']?.value || 0;
          planValue = Number(ordersCountPlan) * Number(pricePlan);
        } else if (metricKey === 'buyoutsSumRub') {
          // Use calculated plan: buyoutsCount × price
          const buyoutsCountPlan =
            planEditStates[productId]?.[currentMonth]?.['buyoutsCount']?.value || 0;
          const pricePlan = planEditStates[productId]?.[currentMonth]?.['price']?.value || 0;
          planValue = Number(buyoutsCountPlan) * Number(pricePlan);
        } else {
          // Use regular plan value from planEditStates
          const editState = planEditStates[productId]?.[currentMonth]?.[metricKey] || {
            isEditing: false,
            value: 0,
          };
          planValue = editState.value ? Number(editState.value) : 0;
        }
        const factValue = record.fact ? Number(record.fact) : 0;

        const today = dayjs();
        const currentMonthDayjs = dayjs(currentMonth, 'YYYY-MM');
        if (!currentMonthDayjs.isSame(today, 'month')) return '';

        const remainingDays = currentMonthDayjs.daysInMonth() - today.date();
        if (remainingDays <= 0) return '';

        // Check for required plan fields that need "Заполни план" message
        const requiredPlanFields = ['ordersCount', 'buyoutsCount', 'price'];
        if (requiredPlanFields.includes(record.key) && planValue <= 0) {
          return (
            <span style={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '12px' }}>
              Заполни план
            </span>
          );
        }

        // Special handling for price - no calculations, just empty when set
        if (record.key === 'price') {
          return '';
        }

        const dailyPlan = (planValue - factValue) / remainingDays;
        const roundedDailyPlan = Math.round(dailyPlan);

        // Check for exceeded plans (negative daily plan) for specific rows
        const exceededCheckFields = [
          'ordersCount',
          'buyoutsCount',
          'ordersSumRub',
          'buyoutsSumRub',
        ];
        if (exceededCheckFields.includes(record.key) && roundedDailyPlan < 0) {
          return <span>Превышен</span>;
        }

        if (record.key === 'costPerAdvertisingOrder') {
          let backgroundColor = 'transparent';
          if (planValue > 0) {
            backgroundColor = roundedDailyPlan > planValue ? '#ffcdd2' : '#a5d6a7';
          }
          return (
            <div
              style={{
                backgroundColor,
                padding: '0 4px',
                borderRadius: '2px',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              {roundedDailyPlan}
            </div>
          );
        }
        return roundedDailyPlan;
      },
    },
  ];

  const daysInMonth = dayjs(currentMonth, 'YYYY-MM').daysInMonth();
  for (let day = 1; day <= daysInMonth; day++) {
    const dataIndex = `day${day}`;
    columns.push({
      title: String(day),
      dataIndex,
      key: `day${day}`,
      width: 60,
      align: 'right' as const,
      render: (value: number | string) => value || 0,
      onCell: (record: TableDataTypeWithDays) => {
        const style: React.CSSProperties = {};
        if (record.key === 'costPerAdvertisingOrder') {
          const dailyValue = Number(record[dataIndex as keyof TableDataTypeWithDays] ?? 0);

          // Check if orders are 0 while cost per order is not 0 on the same day
          const dayStat = statsByDate[String(day)];
          const ordersValue = dayStat ? Number(dayStat.orders) || 0 : 0;

          if (ordersValue === 0 && dailyValue !== 0) {
            style.backgroundColor = '#ffcdd2'; // Red background
          } else {
            // Original plan vs actual logic
            const editState = planEditStates[productId]?.[currentMonth]?.[record.key] || {
              isEditing: false,
              value: 0,
            };
            const planValue = editState.value ? Number(editState.value) : 0;
            if (planValue > 0) {
              style.backgroundColor = dailyValue > planValue ? '#ffcdd2' : '#a5d6a7';
            }
          }
        }
        if (borderKeys.includes(record.key)) {
          style.borderBottom = '2px solid #555';
        }
        return { style };
      },
    });
  }
  return columns;
};

interface ProductStatsTableProps {
  productData: ProductStatsData;
  planEditStates: PlanEditStates;
  toggleEditMode: (productId: number, monthKey: string, metricKey: string) => void;
  updatePlanValue: (productId: number, monthKey: string, metricKey: string, value: string) => void;
  selectedRows: Record<string, string | null>;
  setSelectedRows: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
}

export const ProductStatsTable: React.FC<ProductStatsTableProps> = ({
  productData,
  planEditStates,
  toggleEditMode,
  updatePlanValue,
  selectedRows,
  setSelectedRows,
}) => {
  const sortedMonthKeys = getSortedMonthKeys(productData.monthsData);

  if (sortedMonthKeys.length === 0) {
    return (
      <Card
        key={productData.productId}
        style={{ marginBottom: '30px' }}
        title={`${productData.productName} (ID: ${productData.productId})`}
      >
        <Paragraph>Нет данных о статистике для этого товара.</Paragraph>
      </Card>
    );
  }

  const monthTabs = sortedMonthKeys.map((monthKey) => ({
    key: monthKey,
    label: getMonthNameInRussian(monthKey),
    children: (
      <div>
        <Table
          columns={generateTableColumns(
            monthKey,
            productData.productId,
            planEditStates,
            updatePlanValue,
            toggleEditMode,
            productData.monthsData[monthKey]
          )}
          dataSource={generateTableDataSource(
            productData.monthsData[monthKey],
            monthKey,
            productData.productId,
            planEditStates
          )}
          bordered
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          style={{ width: '100%' }}
          className="compact-stats-table"
          rowClassName={(record) => {
            const tableKey = `${productData.productId}-${monthKey}`;
            const isSelected = selectedRows[tableKey] === record.key;

            let baseClass = '';
            if (record.rowType === 'campaign') {
              baseClass = 'campaign-metric-row';
            } else if (record.rowType === 'nm') {
              baseClass = 'nm-metric-row';
            } else if (record.rowType === 'calculated') {
              baseClass = 'calculated-metric-row';
            } else if (record.rowType === 'percentage') {
              baseClass = 'percentage-metric-row';
            }

            return isSelected ? `${baseClass} highlighted-row` : baseClass;
          }}
          onRow={(record) => ({
            onClick: () => {
              const tableKey = `${productData.productId}-${monthKey}`;
              setSelectedRows((prev) => ({
                ...prev,
                [tableKey]: prev[tableKey] === record.key ? null : record.key,
              }));
            },
            style: { cursor: 'pointer' },
          })}
        />
      </div>
    ),
  }));

  return (
    <Card
      key={productData.productId}
      style={{ marginBottom: '30px' }}
      title={`${productData.productName} (ID: ${productData.productId})`}
    >
      <Tabs defaultActiveKey={sortedMonthKeys[0]} items={monthTabs} size="small" />
    </Card>
  );
};
